import { POSType } from '@digilize/shared/definitions/src';

import { CartSummaryProductDto } from '../infrastructure/dto/products.dto';
import {
  SubscriptionBillingPeriodTypeDto,
  SubscriptionStartDateRequirementDto,
  SubscriptionTypeDto,
} from '../infrastructure/dto/subscription.dto';

/** Domain re-exports so pages/state don't import DTO enums directly. */
export type SubscriptionPeriodType = SubscriptionBillingPeriodTypeDto;
export const SubscriptionPeriodType = SubscriptionBillingPeriodTypeDto;
export type SubscriptionType = SubscriptionTypeDto;
export const SubscriptionType = SubscriptionTypeDto;
export type SubscriptionStartDateRequirement = SubscriptionStartDateRequirementDto;
export const SubscriptionStartDateRequirement = SubscriptionStartDateRequirementDto;

export interface SubscriptionBillingPeriod {
  id: string;
  periodType: SubscriptionPeriodType;
  price: number;
  priceBefore: number | null;
  taxRate: number | null;
  /** Backend-computed per-month equivalent of `price` — the "only €X per month" line. */
  pricePerMonth: number | null;
  /** Backend-computed whole-percent saving vs. the worst per-month rate; null when there is nothing to save. */
  savingsPercentage: number | null;

  firstAllowedStartDate: string | null;
}

export interface SubscriptionProduct {
  id: string;
  name: string;
  /** Tagline shown under the product name ("Unlimited movies during weekdays…"); null until set in the dashboard. */
  subtitle: string | null;
  description: string | null;
  type: SubscriptionType;
  requiresAccount: boolean;
  /** Whether the buyer picks the start date at checkout — see {@link startDatePickerShown}. */
  startDateRequirement: SubscriptionStartDateRequirement;
  photoUrl: string | null;
  photoAlt: string | null;
  price: number;
  priceBefore: number | null;
  taxRate: number | null;
  /** Ordered shortest-period-first by the backend to match the grid — render as returned. */
  billingPeriods: SubscriptionBillingPeriod[];
  sepaDirectDebitEnabled: boolean;
  sepaTermsAndConditionsUrl: string | null;
  sepaDescriptionHtml: string | null;
  isSoldOut: boolean;
}

/**
 * A recurring subscription is billed per period and carries billing periods; a passepartout or
 * punchcard is paid once and has none. Every difference between the two checkout flows — the
 * billing-period step, the per-period price on the summary row — hangs off this.
 */
export function isRecurringSubscription(type: SubscriptionType): boolean {
  return type === SubscriptionType.RecurringSubscription;
}

/**
 * Whether buying this subscription establishes a SEPA direct-debit mandate. Only a recurring
 * subscription can: the mandate collects the following periods, and the flag says the product is
 * configured for it. A mandate carries a single amount and no product basket, which is why such an
 * order may not hold anything else — see {@link CartIsolation}.
 */
export function requiresSepaMandate(product: SubscriptionProduct): boolean {
  return isRecurringSubscription(product.type) && product.sepaDirectDebitEnabled;
}

/**
 * Whether buying this subscription forces the buyer to have an account. A recurring subscription
 * always does — it is billed period after period and managed from the web portal — while a
 * passepartout or punchcard is a one-time purchase that only requires one when the product is
 * configured for it (`requires_account`, usually off). Everything else on a prepaid order, gift
 * cards above all, may be bought without an account; see {@link AccountRequirement}.
 */
export function subscriptionRequiresAccount(product: SubscriptionProduct): boolean {
  return isRecurringSubscription(product.type) || product.requiresAccount;
}

/**
 * Whether the checkout puts a start-date picker to the buyer. `NotRequired` hides it and the line is
 * submitted on its first allowed day; the other two levels show it, empty being allowed only on
 * `Optional` — which lands on that same day. Relevant to all three subscription types — a punchcard's
 * and a passepartout's validity period is counted from the start date too — which is why the setting
 * lives on the product's Rules step rather than its recurring-only contract terms.
 */
export function startDatePickerShown(product: SubscriptionProduct): boolean {
  return product.startDateRequirement !== SubscriptionStartDateRequirement.NotRequired;
}

/**
 * Whether the buyer must choose a start date before the order can be created. The server refuses
 * such an order with `subscription_start_date_required`; this is what lets the shop say so first.
 */
export function startDateRequired(product: SubscriptionProduct): boolean {
  return product.startDateRequirement === SubscriptionStartDateRequirement.Required;
}

export function cartRequiresAccount(products: CartSummaryProductDto[] | null | undefined): boolean | null {
  if (!products?.length) {
    return null;
  }
  return products.some((product) => product.requires_account === true);
}

/**
 * Which subscription types a POS may sell, mirroring the rule the dashboard applies when the POS is
 * configured (`form-pos-add-ons.component.ts`): the Subscriptions online shop is recurring-only and
 * the Prepaid online shop sells passepartouts and punchcards. `null` means no restriction, so an
 * unexpected POS type shows whatever the backend returned instead of an empty shop.
 */
export function allowedSubscriptionTypesForPos(posType: POSType): SubscriptionType[] | null {
  switch (posType) {
    case POSType.Subscription:
      return [SubscriptionType.RecurringSubscription];
    case POSType.Physical:
    case POSType.PrepaidOnline:
      return [SubscriptionType.Passepartout, SubscriptionType.Punchcard];
    default:
      return null;
  }
}

/**
 * POS types whose orders are confirmed inside the subscription checkout
 * (`:posId/sub-checkout/subs-order/:orderId`) instead of the ticket-flow `/order/:orderId` page.
 * `Physical` also sells passepartouts and punchcards (see {@link allowedSubscriptionTypesForPos})
 * but has no web checkout, so it never produces a payment-provider redirect back into this app.
 */
export function usesSubscriptionCheckout(posType: POSType): boolean {
  return posType === POSType.Subscription || posType === POSType.PrepaidOnline;
}

/**
 * Reads a billing period type out of a URL, as the `?billing=` deep link writes it: the swagger
 * enum's name (`yearly`, `halfyearly`, `quarterly`, `monthly`) rather than the integer the API
 * carries, so the link stays readable and survives an enum renumbering. Anything else — a stale
 * link still passing a billing period id, a typo — resolves to null and is ignored by the caller.
 *
 * Beware the `Yearly = 0` member: test the result against null, never for truthiness.
 */
export function parseSubscriptionPeriodType(value: string | null | undefined): SubscriptionPeriodType | null {
  switch (value?.trim().toLowerCase()) {
    case 'yearly':
      return SubscriptionPeriodType.Yearly;
    case 'halfyearly':
      return SubscriptionPeriodType.Halfyearly;
    case 'quarterly':
      return SubscriptionPeriodType.Quarterly;
    case 'monthly':
      return SubscriptionPeriodType.Monthly;
    default:
      return null;
  }
}

/** Transloco key for a billing period type, e.g. SubscriptionPeriodType.Monthly -> 'subscriptions.period.monthly'. */
export function subscriptionPeriodLabelKey(type: SubscriptionPeriodType): string {
  switch (type) {
    case SubscriptionPeriodType.Yearly:
      return 'subscriptions.period.yearly';
    case SubscriptionPeriodType.Halfyearly:
      return 'subscriptions.period.halfyearly';
    case SubscriptionPeriodType.Quarterly:
      return 'subscriptions.period.quarterly';
    case SubscriptionPeriodType.Monthly:
      return 'subscriptions.period.monthly';
    default:
      return '';
  }
}

/** Transloco key for a billing period card header, e.g. Monthly -> 'subscriptions.period_selection.pay.monthly'. */
export function subscriptionPeriodPayLabelKey(type: SubscriptionPeriodType): string {
  switch (type) {
    case SubscriptionPeriodType.Yearly:
      return 'subscriptions.period_selection.pay.yearly';
    case SubscriptionPeriodType.Halfyearly:
      return 'subscriptions.period_selection.pay.halfyearly';
    case SubscriptionPeriodType.Quarterly:
      return 'subscriptions.period_selection.pay.quarterly';
    case SubscriptionPeriodType.Monthly:
    default:
      return 'subscriptions.period_selection.pay.monthly';
  }
}

/** Transloco key for a billing period card price caption, e.g. Yearly -> 'subscriptions.period_selection.price_per.yearly'. */
export function subscriptionPeriodPriceCaptionKey(type: SubscriptionPeriodType): string {
  switch (type) {
    case SubscriptionPeriodType.Yearly:
      return 'subscriptions.period_selection.price_per.yearly';
    case SubscriptionPeriodType.Halfyearly:
      return 'subscriptions.period_selection.price_per.halfyearly';
    case SubscriptionPeriodType.Quarterly:
      return 'subscriptions.period_selection.price_per.quarterly';
    case SubscriptionPeriodType.Monthly:
    default:
      return 'subscriptions.period_selection.price_per.monthly';
  }
}
